/**
 * Settings › Organization › Dashboard — which widgets the company's Overview
 * shows, and in what order. One layout per company, set by its admin.
 *
 *   GET  /dashboard/layout         the saved (or default) layout + the catalogue
 *   PUT  /dashboard/layout         admin — save
 *   POST /dashboard/layout/reset   admin — delete the saved row, back to defaults
 *
 * The list is every AVAILABLE widget this company's verticals allow, in the
 * saved order, with the ones the layout does not carry appended switched off.
 * Action Queue is locked: on, first, and neither toggle nor arrows move it — the
 * API refuses a layout that says otherwise. Phase 2 and 3 widgets are listed
 * below as "coming soon" and cannot be switched on.
 *
 * A widget the API marks `is_new` carries a "New" badge beside its name. The
 * API decides it (newly available, within 60 days, and not switched on here);
 * this page only draws it, so it never guesses from dates of its own.
 *
 * Saving and resetting invalidate the layout query, so the Overview redraws
 * from the API's answer rather than from what this page sent.
 *
 * The tab is `adminOnly`; a direct visit by anyone else still renders, read
 * only, and says who can change it.
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Lock, RotateCcw, Save } from 'lucide-react';
import { toast } from 'sonner';
import { api, getApiErrorMessage } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { isAdminRole } from '../../lib/roles';
import { unwrap } from '../../lib/api-envelope';
import { setQueryDataMapped } from '../../lib/queryClient';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { layoutWrite, moveRow, pickerRows, type PickerRow } from '../../lib/overview-layout';
import { DASHBOARD_LAYOUT_QUERY_KEY, useDashboardLayout } from '../../hooks/useDashboardLayout';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import type { DashboardLayout } from '../../types/dashboard-layout';

const SIZE_LABEL: Record<string, string> = { full: 'Full width', half: 'Half width' };
const sameRows = (a: PickerRow[], b: PickerRow[]) =>
  a.length === b.length && a.every((r, i) => r.key === b[i].key && r.enabled === b[i].enabled);

export default function DashboardLayoutPage() {
  const { user } = useAuth();
  const canEdit = isAdminRole(user?.role);
  const queryClient = useQueryClient();
  const layoutQ = useDashboardLayout();
  const layout = layoutQ.data;
  const failure = layout === undefined ? queryLoadFailure(layoutQ) : null;

  const saved = useMemo(() => (layout ? pickerRows(layout.widgets, layout.catalogue) : []), [layout]);
  const [rows, setRows] = useState<PickerRow[]>([]);
  useEffect(() => setRows(saved), [saved]);
  const dirty = !sameRows(rows, saved);
  const sizeOf = useMemo(() => new Map((layout?.catalogue ?? []).map((c) => [c.key, c.size])), [layout]);
  const comingSoon = (layout?.catalogue ?? []).filter((c) => !c.available);

  const settle = async (next: DashboardLayout | null) => {
    if (next) setQueryDataMapped<DashboardLayout>(queryClient, DASHBOARD_LAYOUT_QUERY_KEY, next);
    await queryClient.invalidateQueries({ queryKey: DASHBOARD_LAYOUT_QUERY_KEY });
  };
  const save = useMutation({
    mutationFn: () => api.dashboard.saveLayout(layoutWrite(rows, layout!.version)).then((r) => unwrap<DashboardLayout>(r.data)),
    onSuccess: async (next) => { await settle(next); toast.success('Overview layout saved'); },
    onError: (err) => toast.error(getApiErrorMessage(err, 'The layout could not be saved.')),
  });
  const reset = useMutation({
    mutationFn: () => api.dashboard.resetLayout().then((r) => unwrap<DashboardLayout>(r.data)),
    onSuccess: async (next) => { await settle(next); toast.success('Overview reset to the default layout'); },
    onError: (err) => toast.error(getApiErrorMessage(err, 'The layout could not be reset.')),
  });
  const busy = save.isPending || reset.isPending;

  const toggle = (key: string) =>
    setRows((rs) => rs.map((r) => (r.key === key && !r.locked ? { ...r, enabled: !r.enabled } : r)));

  return (
    <div className="min-h-full" data-testid="dashboard-layout-page">
      {/* The Organization workspace renders the title and the tab strip. */}
      <div className="space-y-6 px-6 py-4">
        <div>
          <h3 className="text-sm font-semibold text-fieldiq-text">Overview widgets</h3>
          <p className="mt-0.5 text-xs text-fieldiq-muted">
            Choose what everyone in your company sees on the Overview, and in what order. Each person still sees
            only the widgets their role can open.
          </p>
        </div>

        {failure ? (
          <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card">
            <LoadFailureNotice error={queryFailureError(layoutQ)} kind={failure} subject="the Overview layout" />
          </div>
        ) : !layout ? (
          <div className="space-y-2" aria-busy="true">
            {[0, 1, 2, 3].map((i) => <div key={i} className="h-12 animate-pulse rounded-fi-control bg-fieldiq-card" />)}
          </div>
        ) : (
          <>
            <ol className="divide-y divide-fieldiq-border overflow-hidden rounded-fi-panel border border-fieldiq-border bg-fieldiq-card" aria-label="Overview widgets, in order">
              {rows.map((r, i) => {
                const upBlocked = !canEdit || r.locked || i === 0 || rows[i - 1]?.locked;
                const downBlocked = !canEdit || r.locked || i === rows.length - 1;
                return (
                  <li key={r.key} data-testid={`layout-row-${r.key}`} className="flex items-center gap-3 px-3 py-2.5">
                    <div className="flex flex-col">
                      <button type="button" aria-label={`Move ${r.label} up`} disabled={upBlocked || busy} onClick={() => setRows((rs) => moveRow(rs, r.key, -1))} className="rounded p-0.5 text-fieldiq-muted hover:text-fieldiq-text disabled:opacity-30">
                        <ArrowUp className="size-3.5" aria-hidden="true" />
                      </button>
                      <button type="button" aria-label={`Move ${r.label} down`} disabled={downBlocked || busy} onClick={() => setRows((rs) => moveRow(rs, r.key, 1))} className="rounded p-0.5 text-fieldiq-muted hover:text-fieldiq-text disabled:opacity-30">
                        <ArrowDown className="size-3.5" aria-hidden="true" />
                      </button>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="flex min-w-0 items-center gap-2 text-sm font-semibold text-fieldiq-text">
                        <span className="truncate">{r.label}</span>
                        {r.isNew && (
                          <span
                            data-testid={`layout-new-${r.key}`}
                            className="shrink-0 rounded-fi-pill border border-black/20 bg-fi-signal-amber px-1.5 py-px text-[0.6rem] font-black uppercase tracking-wide text-fi-signal-ink"
                          >
                            New
                          </span>
                        )}
                      </p>
                      <p className="text-[11px] text-fieldiq-muted">{SIZE_LABEL[sizeOf.get(r.key) ?? ''] ?? '—'}</p>
                    </div>
                    {r.locked ? (
                      <span data-testid="layout-locked" className="inline-flex items-center gap-1 text-xs font-semibold text-fieldiq-muted">
                        <Lock className="size-3.5" aria-hidden="true" /> Always on
                      </span>
                    ) : (
                      <button
                        type="button"
                        role="switch"
                        aria-checked={r.enabled}
                        aria-label={`Show ${r.label}`}
                        disabled={!canEdit || busy}
                        onClick={() => toggle(r.key)}
                        className={`relative h-6 w-11 shrink-0 rounded-full border border-black/20 transition-colors disabled:opacity-50 ${r.enabled ? 'bg-fi-signal-green' : 'bg-fieldiq-dark'}`}
                      >
                        <span className={`absolute top-0.5 size-[18px] rounded-full bg-fieldiq-card shadow transition-all ${r.enabled ? 'left-[22px]' : 'left-0.5'}`} />
                      </button>
                    )}
                  </li>
                );
              })}
            </ol>

            {comingSoon.length > 0 && (
              <div>
                <p className="mb-2 text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Coming soon</p>
                <ul className="divide-y divide-fieldiq-border overflow-hidden rounded-fi-panel border border-fieldiq-border bg-fieldiq-card">
                  {comingSoon.map((c) => (
                    <li key={c.key} data-testid={`layout-soon-${c.key}`} className="flex items-center gap-3 px-3 py-2.5 opacity-60">
                      <p className="flex-1 text-sm text-fieldiq-text">{c.label}</p>
                      <span className="rounded-fi-pill border border-black/20 bg-fi-signal-slate px-2 py-0.5 text-[10px] font-black uppercase text-fi-signal-ink">Coming soon</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {canEdit ? (
              <div className="flex flex-wrap items-center gap-3">
                <button type="button" onClick={() => save.mutate()} disabled={!dirty || busy} className="fi-action fi-action--standard fi-action--primary disabled:opacity-50">
                  <Save className="size-4" aria-hidden="true" /> {save.isPending ? 'Saving…' : 'Save'}
                </button>
                <button type="button" onClick={() => reset.mutate()} disabled={busy || layout.source === 'default'} className="fi-action fi-action--standard border border-fieldiq-border bg-fieldiq-card text-fieldiq-text disabled:opacity-50">
                  <RotateCcw className="size-4" aria-hidden="true" /> Reset to default
                </button>
                <p className="text-xs text-fieldiq-muted">
                  {layout.source === 'default' ? 'Showing the default layout for your company.' : 'Your company has a saved layout.'}
                </p>
              </div>
            ) : (
              <p className="text-xs text-fieldiq-muted">Only an admin can change the Overview layout.</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
